import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { 
  Calendar, Users, FileText, MapPin, Clock, CheckCircle, XCircle, 
  Eye, Trash2, RefreshCw, ChevronDown, Plus, Edit
} from "lucide-react";
import { PageHeader } from "@/components/layout/Navigation";
import { toast } from "sonner";
import api from "@/lib/api";

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [consultations, setConsultations] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [blogPosts, setBlogPosts] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [statsRes, consultRes, apptRes, blogRes, locRes] = await Promise.all([
        api.getAdminStats(),
        api.getConsultations(),
        api.getAppointments(),
        api.getBlogPosts(null, 100),
        api.getLocations()
      ]);
      setStats(statsRes.data);
      setConsultations(consultRes.data);
      setAppointments(apptRes.data);
      setBlogPosts(blogRes.data);
      setLocations(locRes.data);
    } catch (error) {
      console.error(error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const updateConsultationStatus = async (id, status) => {
    try {
      await api.updateConsultation(id, { status });
      toast.success(`Consultation marked as ${status}`);
      loadData();
    } catch (error) {
      toast.error("Failed to update consultation");
    }
  };

  const updateAppointmentStatus = async (id, status) => {
    try {
      await api.updateAppointment(id, { status });
      toast.success(`Appointment ${status}`);
      loadData();
    } catch (error) {
      toast.error("Failed to update appointment");
    }
  };

  const deleteConsultation = async (id) => {
    if (!window.confirm("Are you sure you want to delete this consultation?")) return;
    try {
      await api.deleteConsultation(id);
      toast.success("Consultation deleted");
      loadData();
    } catch (error) {
      toast.error("Failed to delete consultation");
    }
  };

  const tabs = [
    { id: "overview", label: "Overview", icon: Eye },
    { id: "consultations", label: "Consultations", icon: Users },
    { id: "appointments", label: "Appointments", icon: Calendar },
    { id: "blog", label: "Blog Posts", icon: FileText },
    { id: "locations", label: "Locations", icon: MapPin },
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-[#1B4D3E] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard">
      <PageHeader 
        title="Admin Dashboard"
        subtitle="Manage consultations, appointments, blog posts, and locations."
        breadcrumbs={[{ name: "Admin" }]}
      />

      <section className="py-12 bg-[#F9F9F7] min-h-screen">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          {/* Tabs */}
          <div className="flex flex-wrap gap-2 mb-8">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-6 py-3 rounded-full text-sm font-medium transition-all ${
                  activeTab === tab.id 
                    ? "bg-[#1B4D3E] text-white" 
                    : "bg-white text-[#4A4A4A] hover:bg-[#1B4D3E]/5"
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
            <button onClick={loadData} className="ml-auto flex items-center gap-2 px-4 py-3 text-[#1B4D3E]">
              <RefreshCw className="w-4 h-4" /> Refresh
            </button>
          </div>

          {/* Overview Tab */}
          {activeTab === "overview" && stats && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <div className="bg-white rounded-2xl p-6 border border-[#1B4D3E]/10">
                  <div className="flex items-center justify-between mb-4">
                    <Users className="w-8 h-8 text-[#1B4D3E]" />
                    <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded-full">
                      {stats.consultations.pending} pending
                    </span>
                  </div>
                  <p className="text-3xl font-display text-[#1A1A1A]">{stats.consultations.total}</p>
                  <p className="text-sm text-[#888]">Total Consultations</p>
                </div>
                <div className="bg-white rounded-2xl p-6 border border-[#1B4D3E]/10">
                  <div className="flex items-center justify-between mb-4">
                    <Calendar className="w-8 h-8 text-[#1B4D3E]" />
                    <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
                      {stats.appointments.upcoming} upcoming
                    </span>
                  </div>
                  <p className="text-3xl font-display text-[#1A1A1A]">{stats.appointments.total}</p>
                  <p className="text-sm text-[#888]">Total Appointments</p>
                </div>
                <div className="bg-white rounded-2xl p-6 border border-[#1B4D3E]/10">
                  <FileText className="w-8 h-8 text-[#1B4D3E] mb-4" />
                  <p className="text-3xl font-display text-[#1A1A1A]">{stats.blog_posts}</p>
                  <p className="text-sm text-[#888]">Blog Posts</p>
                </div>
                <div className="bg-white rounded-2xl p-6 border border-[#1B4D3E]/10">
                  <MapPin className="w-8 h-8 text-[#1B4D3E] mb-4" />
                  <p className="text-3xl font-display text-[#1A1A1A]">{stats.locations}</p>
                  <p className="text-sm text-[#888]">Active Locations</p>
                </div>
              </div>

              {/* Recent Activity */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="bg-white rounded-2xl p-6 border border-[#1B4D3E]/10">
                  <h3 className="font-display text-xl text-[#1A1A1A] mb-4">Recent Consultations</h3>
                  <div className="space-y-3">
                    {consultations.slice(0, 5).map(c => (
                      <div key={c.id} className="flex items-center justify-between py-3 border-b border-[#1B4D3E]/5 last:border-0">
                        <div>
                          <p className="font-medium text-[#1A1A1A]">{c.first_name} {c.last_name}</p>
                          <p className="text-sm text-[#888]">{c.service_interest}</p>
                        </div>
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          c.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                          c.status === 'contacted' ? 'bg-blue-100 text-blue-700' :
                          'bg-green-100 text-green-700'
                        }`}>
                          {c.status}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="bg-white rounded-2xl p-6 border border-[#1B4D3E]/10">
                  <h3 className="font-display text-xl text-[#1A1A1A] mb-4">Upcoming Appointments</h3>
                  <div className="space-y-3">
                    {appointments.filter(a => a.status === 'scheduled').slice(0, 5).map(a => (
                      <div key={a.id} className="flex items-center justify-between py-3 border-b border-[#1B4D3E]/5 last:border-0">
                        <div>
                          <p className="font-medium text-[#1A1A1A]">{a.first_name} {a.last_name}</p>
                          <p className="text-sm text-[#888]">{a.appointment_date} at {a.appointment_time}</p>
                        </div>
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
                          Scheduled
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* Consultations Tab */}
          {activeTab === "consultations" && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <div className="bg-white rounded-2xl border border-[#1B4D3E]/10 overflow-hidden">
                <table className="w-full">
                  <thead className="bg-[#F9F9F7]">
                    <tr>
                      <th className="text-left p-4 text-sm font-medium text-[#4A4A4A]">Name</th>
                      <th className="text-left p-4 text-sm font-medium text-[#4A4A4A]">Contact</th>
                      <th className="text-left p-4 text-sm font-medium text-[#4A4A4A]">Service</th>
                      <th className="text-left p-4 text-sm font-medium text-[#4A4A4A]">Status</th>
                      <th className="text-left p-4 text-sm font-medium text-[#4A4A4A]">Date</th>
                      <th className="text-left p-4 text-sm font-medium text-[#4A4A4A]">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {consultations.map(c => (
                      <tr key={c.id} className="border-t border-[#1B4D3E]/5" data-testid={`consultation-row-${c.id}`}>
                        <td className="p-4">
                          <p className="font-medium text-[#1A1A1A]">{c.first_name} {c.last_name}</p>
                        </td>
                        <td className="p-4">
                          <p className="text-sm text-[#4A4A4A]">{c.email}</p>
                          <p className="text-sm text-[#888]">{c.phone}</p>
                        </td>
                        <td className="p-4 text-sm text-[#4A4A4A]">{c.service_interest}</td>
                        <td className="p-4">
                          <select
                            value={c.status}
                            onChange={(e) => updateConsultationStatus(c.id, e.target.value)}
                            className={`text-xs px-3 py-1.5 rounded-full border-none cursor-pointer ${
                              c.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                              c.status === 'contacted' ? 'bg-blue-100 text-blue-700' :
                              'bg-green-100 text-green-700'
                            }`}
                          >
                            <option value="pending">Pending</option>
                            <option value="contacted">Contacted</option>
                            <option value="converted">Converted</option>
                          </select>
                        </td>
                        <td className="p-4 text-sm text-[#888]">
                          {new Date(c.created_at).toLocaleDateString()}
                        </td>
                        <td className="p-4">
                          <button 
                            onClick={() => deleteConsultation(c.id)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </motion.div>
          )}

          {/* Appointments Tab */}
          {activeTab === "appointments" && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <div className="bg-white rounded-2xl border border-[#1B4D3E]/10 overflow-hidden">
                <table className="w-full">
                  <thead className="bg-[#F9F9F7]">
                    <tr>
                      <th className="text-left p-4 text-sm font-medium text-[#4A4A4A]">Patient</th>
                      <th className="text-left p-4 text-sm font-medium text-[#4A4A4A]">Contact</th>
                      <th className="text-left p-4 text-sm font-medium text-[#4A4A4A]">Date & Time</th>
                      <th className="text-left p-4 text-sm font-medium text-[#4A4A4A]">Status</th>
                      <th className="text-left p-4 text-sm font-medium text-[#4A4A4A]">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {appointments.map(a => (
                      <tr key={a.id} className="border-t border-[#1B4D3E]/5" data-testid={`appointment-row-${a.id}`}>
                        <td className="p-4">
                          <p className="font-medium text-[#1A1A1A]">{a.first_name} {a.last_name}</p>
                        </td>
                        <td className="p-4">
                          <p className="text-sm text-[#4A4A4A]">{a.email}</p>
                          <p className="text-sm text-[#888]">{a.phone}</p>
                        </td>
                        <td className="p-4">
                          <p className="font-medium text-[#1A1A1A]">{a.appointment_date}</p>
                          <p className="text-sm text-[#888]">{a.appointment_time}</p>
                        </td>
                        <td className="p-4">
                          <select
                            value={a.status}
                            onChange={(e) => updateAppointmentStatus(a.id, e.target.value)}
                            className={`text-xs px-3 py-1.5 rounded-full border-none cursor-pointer ${
                              a.status === 'scheduled' ? 'bg-green-100 text-green-700' :
                              a.status === 'completed' ? 'bg-blue-100 text-blue-700' :
                              'bg-red-100 text-red-700'
                            }`}
                          >
                            <option value="scheduled">Scheduled</option>
                            <option value="completed">Completed</option>
                            <option value="cancelled">Cancelled</option>
                          </select>
                        </td>
                        <td className="p-4">
                          <button 
                            onClick={() => api.deleteAppointment(a.id).then(loadData)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </motion.div>
          )}

          {/* Blog Tab */}
          {activeTab === "blog" && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {blogPosts.map(post => (
                  <div key={post.id} className="bg-white rounded-2xl overflow-hidden border border-[#1B4D3E]/10">
                    <img src={post.image_url} alt={post.title} className="w-full h-40 object-cover" />
                    <div className="p-6">
                      <span className="text-xs text-[#D4AF37] font-medium">{post.category}</span>
                      <h4 className="font-display text-lg text-[#1A1A1A] mt-2 mb-2">{post.title}</h4>
                      <p className="text-sm text-[#888] mb-4">By {post.author} • {post.views} views</p>
                      <div className="flex gap-2">
                        <button className="text-sm px-4 py-2 bg-[#1B4D3E]/5 text-[#1B4D3E] rounded-lg">
                          <Edit className="w-4 h-4" />
                        </button>
                        <button 
                          onClick={() => api.deleteBlogPost(post.id).then(loadData)}
                          className="text-sm px-4 py-2 bg-red-50 text-red-500 rounded-lg"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          {/* Locations Tab */}
          {activeTab === "locations" && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {locations.map(location => (
                  <div key={location.id} className="bg-white rounded-2xl overflow-hidden border border-[#1B4D3E]/10">
                    <img src={location.image_url} alt={location.name} className="w-full h-48 object-cover" />
                    <div className="p-6">
                      <h4 className="font-display text-xl text-[#1A1A1A] mb-2">{location.name}</h4>
                      <p className="text-sm text-[#4A4A4A] mb-1">{location.address}</p>
                      <p className="text-sm text-[#4A4A4A] mb-3">{location.city}, {location.state} {location.zip_code}</p>
                      <p className="text-sm text-[#888] mb-1">{location.phone}</p>
                      <p className="text-sm text-[#888] mb-4">{location.hours}</p>
                      <div className="flex gap-2">
                        <button className="text-sm px-4 py-2 bg-[#1B4D3E]/5 text-[#1B4D3E] rounded-lg flex items-center gap-2">
                          <Edit className="w-4 h-4" /> Edit
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </div>
      </section>
    </div>
  );
};

export default AdminDashboard;
